
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useStatusTransitions, useUpdateBookingStatus } from "@/hooks/useBookingStatuses";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/components/ui/use-toast";
import { Loader2, ChevronDown, Settings } from "lucide-react";
import { BookingStatusKey } from "@/types/booking-status";

interface AdminStatusUpdateProps {
  bookingId: string;
  currentStatus: string; // Back to generic string
  onStatusUpdate?: () => void;
}

interface StatusUpdateResult {
  success: boolean;
  error?: string;
  old_status?: string;
  new_status?: string;
  booking?: any;
}

const AdminStatusUpdate: React.FC<AdminStatusUpdateProps> = ({
  bookingId,
  currentStatus,
  onStatusUpdate
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  const { user } = useAuth();
  const { data: transitions = [] } = useStatusTransitions(currentStatus);
  const { updateStatus } = useUpdateBookingStatus();

  // Show all available transitions since we removed validation
  const adminTransitions = transitions;

  const handleStatusChange = (newStatus: string) => {
    setSelectedStatus(newStatus);
    setShowConfirmDialog(true);
  };

  const handleConfirmUpdate = async () => {
    if (!selectedStatus || !user?.id) return;

    setIsUpdating(true);
    try {
      const result = await updateStatus(bookingId, selectedStatus, user.id, notes);
      
      // Type the result properly with proper casting
      const typedResult = result as unknown as StatusUpdateResult;
      
      if (typedResult?.success) {
        toast({
          title: "Status Updated",
          description: `Booking status changed to ${selectedStatus}`,
        });
        
        setSelectedStatus('');
        setNotes('');
        setShowConfirmDialog(false);
        
        // Don't trigger automatic refresh - let realtime handle it
        // if (onStatusUpdate) {
        //   onStatusUpdate();
        // }
      } else {
        throw new Error(typedResult?.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update booking status",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (adminTransitions.length === 0) {
    return null;
  }

  const selectedTransition = adminTransitions.find(t => t.to_status === selectedStatus);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="h-8 px-3 text-xs border-dashed hover:border-solid transition-all"
            disabled={isUpdating}
          >
            <Settings className="w-3 h-3 mr-1" />
            Update Status
            <ChevronDown className="w-3 h-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {adminTransitions.map((transition) => (
            <DropdownMenuItem 
              key={transition.to_status} 
              onClick={() => handleStatusChange(transition.to_status)}
              className="cursor-pointer"
            >
              <div className="flex items-center gap-2 w-full">
                <div 
                  className="w-2 h-2 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: transition.color }}
                />
                <span className="text-sm">{transition.display_name}</span>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Status Update</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change the booking status to "{selectedTransition?.display_name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-2">
            <Label htmlFor="status-notes">Notes (optional)</Label>
            <Textarea
              id="status-notes"
              placeholder="Add any notes about this status change..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmUpdate}
              disabled={isUpdating}
            >
              {isUpdating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update Status
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AdminStatusUpdate;
